import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  NavigationCustomizationItemDto,
  PageFilterConfigsDto,
} from '@asset-metrix/portal-gateway-client-js';
import clsx from 'clsx';

import { usePageFilterQuery } from '../../hooks/filter';
import { useActiveCustomPageFilters } from '../../hooks/useActiveCustomPageFilters';
import { useStore } from '../../store';
import { getDefaultComparisonPair } from '../../utils/comparisonDetailPageEntities';
import {
  COMPARISON_ENTITIES_URL_PARAM,
  CUSTOM_FILTER_INSTANCE_ID_SUFFIX,
} from '../../utils/constants';
import { StakeAdjustmentToggle } from '../page/StakeAdjustmentToggle';

import { CustomPageFilters } from './customPageFilter/CustomPageFilters';
import { type FilterPersistenceMode } from './FilterPersistenceIcon';
import { FiltersComponent } from './FiltersComponent';

import styles from './filters.module.scss';

const noop = () => {
  /* no-op: CustomPageFilters returns null when activeCustomFilterConfigs is empty */
};

export type PageFilterProps = {
  activeDynamicNavigationItem?: NavigationCustomizationItemDto;
  className?: string;
  config?: PageFilterConfigsDto;
  embedded?: boolean;
  filterKey: string;
  hasStakeAdjustment?: boolean;
  persistenceMode?: FilterPersistenceMode;
  onPersistenceClick?: () => void;
  pageId: string;
  customPageFilterStoreId?: string | null;
  showCustomPageFilters?: boolean;
  stakeAdjustmentDefaultEnabled?: boolean;
};

export const PageFilters = ({
  activeDynamicNavigationItem,
  className,
  config,
  embedded,
  filterKey,
  hasStakeAdjustment,
  persistenceMode,
  onPersistenceClick,
  pageId,
  customPageFilterStoreId = null,
  showCustomPageFilters = true,
  stakeAdjustmentDefaultEnabled,
}: PageFilterProps) => {
  const { t } = useTranslation();

  const {
    activeCustomFilterConfigs,
    customizationSettled,
    hasActiveCustomPageFilters,
  } = useActiveCustomPageFilters(
    showCustomPageFilters ? config : undefined,
    showCustomPageFilters ? (customPageFilterStoreId ?? filterKey) : null
  );

  const resetFilterSelections = useStore(
    (state) => state.resetFilterSelections
  );
  const setFilterSelectionValue = useStore(
    (state) => state.setFilterSelectionValue
  );
  const [searchParams] = useSearchParams();

  const query = usePageFilterQuery(
    pageId,
    filterKey,
    config?.filters,
    activeDynamicNavigationItem
  );

  const customPageFiltersElement =
    showCustomPageFilters && customizationSettled ? (
      <CustomPageFilters
        activeCustomFilterConfigs={activeCustomFilterConfigs}
        customFilterInstanceId={`${filterKey}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`}
        pageFilterInstanceId={filterKey}
        pageId={pageId}
        showFilterActions={hasActiveCustomPageFilters}
        resetAllPageFilters={noop}
        persistenceMode={persistenceMode}
        onPersistenceClick={onPersistenceClick}
      />
    ) : null;

  const stakeAdjustmentToggle = hasStakeAdjustment ? (
    <StakeAdjustmentToggle
      filterKey={filterKey}
      defaultEnabled={stakeAdjustmentDefaultEnabled ?? false}
    />
  ) : null;

  const wrapContent = (content: React.ReactNode) =>
    embedded ? (
      content
    ) : (
      <div className={clsx(styles['container'], className)}>{content}</div>
    );

  const visibleFilters = config?.filters.filter((f) => !f.hidden);
  const customConfigs = showCustomPageFilters ? activeCustomFilterConfigs : [];

  if (
    config == null ||
    (visibleFilters?.length === 0 && customConfigs.length === 0)
  ) {
    return wrapContent(
      <>
        {customPageFiltersElement}
        {stakeAdjustmentToggle}
      </>
    );
  }

  const resetFilterSelectionWrapper = (pageFilterInstanceId: string) => {
    const hiddenPageFilterKeys = config.filters
      .filter((fil) => fil.hidden)
      .map((f) => f.key);
    resetFilterSelections(
      pageFilterInstanceId,
      undefined,
      hiddenPageFilterKeys
    );

    const comparisonConfig = config.filters.find(
      (filter) => filter.display === 'comparison'
    );
    if (
      comparisonConfig != null &&
      searchParams.has(COMPARISON_ENTITIES_URL_PARAM)
    ) {
      const defaultPair = getDefaultComparisonPair(searchParams);
      if (defaultPair != null) {
        setFilterSelectionValue(
          pageFilterInstanceId,
          comparisonConfig.key,
          defaultPair
        );
      }
    }
  };

  const resetAllPageFilters = (
    pageFilterInstanceId: string,
    customFilterInstanceId: string
  ) => {
    resetFilterSelectionWrapper(pageFilterInstanceId);
    resetFilterSelections(customFilterInstanceId);
  };

  const customPageFiltersWithReset =
    showCustomPageFilters && customizationSettled ? (
      <CustomPageFilters
        activeCustomFilterConfigs={activeCustomFilterConfigs}
        customFilterInstanceId={`${filterKey}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`}
        pageFilterInstanceId={filterKey}
        pageId={pageId}
        showFilterActions={hasActiveCustomPageFilters}
        resetAllPageFilters={resetAllPageFilters}
        persistenceMode={persistenceMode}
        onPersistenceClick={onPersistenceClick}
      />
    ) : null;

  const filterComponents = (
    <>
      <FiltersComponent
        config={{ ...config, filters: visibleFilters }}
        entityId={filterKey}
        error={query.error}
        filterTestId="page-filters"
        isFetching={query.isFetching}
        resetFilterSelection={resetFilterSelectionWrapper}
        translate={t}
        showFilterActions={!hasActiveCustomPageFilters}
        validatedData={query.validatedData}
        persistenceMode={persistenceMode}
        onPersistenceClick={onPersistenceClick}
      />
      {customPageFiltersWithReset}
      {stakeAdjustmentToggle}
    </>
  );

  return wrapContent(filterComponents);
};
